import React, { useState, useEffect, useRef } from 'react';
import Switch from 'react-switch';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { setGroupement, setLogoDisplay, setLogoUrl } from '../userSlice';
import Spinner from '../spinner/Spinner';
import {
    selectDisplayLogoOnPublications,
    selectIsAdmin,
    selectIsSsoUser,
    selectLogoUrl,
    selectUserGroupement,
    selectUserId,
    selectUserMyId,
} from '../selectors/userSelectors';

const LogoSwitch = ({ newStyle = false }: { newStyle?: boolean } = {}) => {
    const dispatch = useDispatch();
    const displayLogo = useSelector(selectDisplayLogoOnPublications);
    const userId = useSelector(selectUserId);
    const userMyId = useSelector(selectUserMyId);
    const isSsoUser = useSelector(selectIsSsoUser);
    const isAdmin = useSelector(selectIsAdmin);
    const userGroupement = useSelector(selectUserGroupement);
    const logoUrl = useSelector(selectLogoUrl);
    const hasSpecificGroupement = userGroupement !== undefined && userGroupement.id !== 1;
    const isAdminGrp = !isAdmin && !isSsoUser && hasSpecificGroupement;
    const initialChecked = isAdminGrp
        ? (userGroupement?.displayLogoOnPublications ?? false)
        : displayLogo;

    const [isChecked, setIsChecked] = useState(initialChecked);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [logoLoading, setLogoLoading] = useState(false);
    const hasSyncedRef = useRef(false);

    useEffect(() => {
        const syncDisplayLogoFromApi = async () => {
            if (!isSsoUser || userId === undefined || isAdminGrp || hasSyncedRef.current) return;

            hasSyncedRef.current = true;

            try {
                const response = await axios.get(`/api/users/${userId}`);
                const apiValue = response.data.displayLogoOnPublications || false;

                if (apiValue !== displayLogo) {
                    dispatch(setLogoDisplay(apiValue));
                    setIsChecked(apiValue);
                }
            } catch (error) {
                console.error('Error syncing displayLogoOnPublications:', error);
            }
        };

        syncDisplayLogoFromApi();
    }, [userId, isSsoUser, isAdminGrp, dispatch, displayLogo]);

    useEffect(() => {
        const fetchUserLogo = async () => {
            if (!isSsoUser || userMyId === undefined || logoUrl !== undefined) return;

            setLogoLoading(true);
            try {
                const response = await axios.get(`/api/my-api-user-logo/${userMyId}`);
                if (response.data.url_user_logo) {
                    dispatch(setLogoUrl(response.data.url_user_logo));
                }
            } catch (error) {
                console.error('Erreur lors de la récupération du logo:', error);
            } finally {
                setLogoLoading(false);
            }
        };

        fetchUserLogo();
    }, [userMyId, isSsoUser, logoUrl, dispatch]);

    const handleChange = async (checked: boolean) => {
        setIsChecked(checked);
        setLoading(true);
        setError(null);

        try {
            if (isAdminGrp && userGroupement) {
                const u = userGroupement;
                await axios.patch(
                    `/api/groupements/${u.id}`,
                    { displayLogoOnPublications: checked },
                    { headers: { 'Content-Type': 'application/merge-patch+json' } },
                );

                dispatch(
                    setGroupement({
                        ...u,
                        displayLogoOnPublications: checked,
                    }),
                );
            } else {
                if (checked && logoUrl === undefined) {
                    setError(
                        'Aucun logo disponible. Veuillez vérifier que votre logo est configuré dans votre espace My.',
                    );
                    setIsChecked(!checked);
                    setLoading(false);
                    return;
                }

                if (userId === undefined) {
                    setLoading(false);
                    return;
                }

                await axios.patch(
                    `/api/users/${userId}`,
                    {
                        displayLogoOnPublications: checked,
                    },
                    {
                        headers: {
                            'Content-Type': 'application/merge-patch+json',
                        },
                    },
                );

                dispatch(setLogoDisplay(checked));
            }
        } catch (error: any) {
            console.error('Error updating logo display:', error);
            setError(error.response?.data?.error || 'Error updating logo display');
            setIsChecked(!checked);
        } finally {
            setLoading(false);
        }
    };

    if (hasSpecificGroupement && isSsoUser && !isAdminGrp) {
        return null;
    }

    if (isSsoUser && !isAdminGrp && logoLoading) {
        return null;
    }

    const switchLabel = isAdminGrp
        ? 'Ajouter les logos de vos agences sur les publications de News Factory'
        : 'Ajouter votre logo sur les publications de News Factory';

    return (
        <div
            style={{
                flex: '0 0 calc(50% - 12px)',
                padding: 20,
                background: 'white',
                borderRadius: 8,
                outline: '1px #E5E5E5 solid',
                outlineOffset: '-1px',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 12,
                display: 'flex',
                marginTop: 32,
            }}
        >
            {loading || logoLoading ? (
                <Spinner loading={loading || logoLoading} />
            ) : (
                <Switch
                    onChange={handleChange}
                    checked={isChecked}
                    uncheckedIcon={false}
                    checkedIcon={false}
                    onColor="#bed000"
                    offColor="#ccc"
                    height={16}
                    width={28}
                    handleDiameter={12}
                />
            )}
            <div
                style={{
                    flex: '1 1 0',
                    flexDirection: 'column',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                    gap: 4,
                    display: 'inline-flex',
                }}
            >
                <div
                    style={{
                        alignSelf: 'stretch',
                        justifyContent: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        color: '#393939',
                        fontSize: 14,
                        fontFamily: 'Montserrat',
                        fontWeight: '400',
                        lineHeight: '18px',
                        wordWrap: 'break-word',
                    }}
                >
                    {switchLabel}
                </div>
                {error && (
                    <div style={{ fontSize: 10, color: '#ef4444', lineHeight: '14px' }}>
                        {error}
                    </div>
                )}
            </div>
        </div>
    );
};

export default LogoSwitch;
